import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { UserPasswordActions } from "@/components/UserPasswordActions";
import { Loader2, Users, AlertTriangle } from "lucide-react";

const ROLES = [
  { key: "", label: "All" },
  { key: "internal", label: "Internal" },
  { key: "client", label: "Clients" },
  { key: "candidate", label: "Candidates" },
  { key: "recruiter_partner", label: "Partners" },
];

const ROLE_COLORS = { internal: "#002EA6", client: "#7C3AED", candidate: "#10B981", recruiter_partner: "#F5A623" };

export default function AdminUsers() {
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users", { params: { role, q } }).then(({ data }) => setData(data));
  }, [role, q]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const users = data?.users || [];
  return (
    <div>
      <PageHeader overline="Accounts & access" title="Users" />
      <div className="px-8 pt-6 flex flex-wrap items-center gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" data-testid="users-search" className="bg-[#F4F4F5] border-black/10 rounded-none max-w-xs" />
        <div className="flex gap-2 flex-wrap">
          {ROLES.map((r) => (
            <button key={r.key} data-testid={`users-role-${r.key || "all"}`} onClick={() => setRole(r.key)}
              className={`text-xs px-3 py-2 border transition-colors ${role === r.key ? "bg-[#09090B] text-white border-[#09090B]" : "border-black/10 text-muted-foreground hover:text-[#09090B]"}`}>{r.label}</button>
          ))}
        </div>
      </div>
      <div className="p-8">
        {!data && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {data && users.length === 0 && <Empty icon={Users} title="No users" hint="No accounts match your filters." />}
        {data && users.length > 0 && (
          <div className="space-y-px bg-black/10 border border-black/10">
            {users.map((u) => (
              <div key={u.id} className="bg-white p-5 flex items-center justify-between gap-4" data-testid={`user-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{u.full_name || "—"}</p>
                    <span className="text-[0.55rem] font-mono px-1.5 border" style={{ color: ROLE_COLORS[u.role] || "#71717A", borderColor: (ROLE_COLORS[u.role] || "#71717A") + "55" }}>{(u.role || "").replace("_", " ").toUpperCase()}</span>
                    {u.must_change_password && <span className="text-[0.55rem] font-mono text-[#F5A623] border border-[#F5A623]/40 px-1.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> MUST RESET</span>}
                  </div>
                  <p className="text-sm text-muted-foreground truncate mt-0.5">{u.email}{u.organization_name ? ` · ${u.organization_name}` : ""}</p>
                </div>
                <div className="shrink-0"><UserPasswordActions userId={u.id} onDone={load} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
